import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { environment } from "../utils/Environment";
import { BotConfigState } from "../store/slices/botConfigSlice";

// Strapi v5 single-type response shape for populate=*
// Fields are flat under data (no attributes wrapper)
interface StrapiMediaV5 {
  id: number;
  url: string;
  [key: string]: any;
}
interface StrapiSingleTypeResponseV5 {
  data: {
    id: number;
    documentId?: string;
    defaultLat?: number | null;
    defaultLng?: number | null;
    veilleActivated?: boolean;
    veilleTimer?: number;
    veilleMuted?: boolean;
    screenSaverActivated?: boolean;
    screenSaverSource?: StrapiMediaV5 | null;
    screenSaverTimeBeforeSwitch?: number;
    slideShowActivated?: boolean;
    slideShowSources?: StrapiMediaV5[] | null;
    slideShowTimeBeforeSwitch?: number;
    infoChannelActivated?: boolean;
  } | null;
}

const BotConfigAPI = createApi({
  reducerPath: "BotConfigAPI",
  baseQuery: fetchBaseQuery({
    baseUrl: `${environment.api_url as string}`,
  }),
  endpoints: (build) => ({
    getBotConfig: build.query<BotConfigState, void>({
      query: () => "/bot-config?populate=*",
      transformResponse: (
        response: StrapiSingleTypeResponseV5,
      ): BotConfigState => {
        // Strapi v5: fields are flat directly under data (no attributes wrapper)
        const attrs = response?.data;
        if (!attrs) {
          return { defaultPosition: null, veille: null };
        }

        const baseUrl = process.env.REACT_APP_BASE_URL ?? "";

        const defaultPosition =
          attrs.defaultLat != null && attrs.defaultLng != null
            ? { lat: attrs.defaultLat, lng: attrs.defaultLng }
            : null;

        const screenSaverSourceUrl = attrs.screenSaverSource?.url
          ? `${baseUrl}${attrs.screenSaverSource.url}`
          : "";

        const slideShowSources: string[] = (attrs.slideShowSources ?? []).map(
          (item) => `${baseUrl}${item.url}`,
        );

        const veille: BotConfigState["veille"] = attrs.veilleActivated
          ? {
              activated: true,
              timer: attrs.veilleTimer ?? 180,
              muted: attrs.veilleMuted ?? false,
              screenSaver:
                attrs.screenSaverActivated && screenSaverSourceUrl
                  ? {
                      activated: true,
                      source: screenSaverSourceUrl,
                      timeBeforeSwitch: attrs.screenSaverTimeBeforeSwitch,
                    }
                  : undefined,
              slideShow:
                attrs.slideShowActivated && slideShowSources.length > 0
                  ? {
                      activated: true,
                      sources: slideShowSources,
                      timeBeforeSwitch: attrs.slideShowTimeBeforeSwitch,
                    }
                  : undefined,
              infoChannel: attrs.infoChannelActivated
                ? { activated: true }
                : undefined,
            }
          : null;

        return { defaultPosition, veille };
      },
    }),
  }),
});

export const { useGetBotConfigQuery } = BotConfigAPI;
export default BotConfigAPI;
